import { useMutation, useQueryClient } from "@tanstack/react-query";
import { communityApi } from "@bw/shared/api";
import { invalidateFeedQueries } from "@bw/shared/hooks";
import { queryKeys } from "../../../common/query/queryClient";
import { INLINE_GUARDIAN_PROMPT } from "../../../common/guardian/denials";

/** The variables a single like toggle carries: which post, and whether it is
 * currently liked BY THE VIEWER (the server's `liked_by_me`) — so the toggle
 * direction is derived from server state, never a local flag. */
export interface LikeVars {
  postId: string;
  liked: boolean;
}

/**
 * Like / unlike toggle (S1a, WRITE). Mirrors useVote: NON-OPTIMISTIC. The
 * displayed like_count is NEVER bumped client-side. On success both post-detail
 * queries and the canonical Feed family are invalidated so the count moves only
 * from a subsequent server response.
 *
 * The same post can render in multiple Feed scopes and in post detail. Both cache
 * families must refresh or stale `liked_by_me` can make Unlike unreachable.
 *
 * The toggle direction comes from the server's `liked_by_me`: a not-yet-liked
 * post calls likePost(postId); an already-liked post calls unlikePost(postId).
 *
 * Re-entrancy is the page's concern: it disables the Like button while
 * `isPending` so a double-click fires the mutation once. A rejected like/unlike
 * (403 blocked / network) leaves the count unchanged and surfaces on
 * `mutation.error` for the page to show honestly via role="alert".
 */
export function useLikePost() {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, LikeVars>({
    meta: INLINE_GUARDIAN_PROMPT,
    mutationFn: ({ postId, liked }: LikeVars) =>
      liked ? communityApi.unlikePost(postId) : communityApi.likePost(postId),
    onSuccess: () => {
      // Refresh post-detail consumers and every canonical Feed scope.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.social.all,
      });
      invalidateFeedQueries(queryClient);
    },
  });
}
